import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import api from "../../../services/api";
import MultiSelect from "../../../components/MultiSelect";
import { FiMail } from "react-icons/fi";
import { AiOutlineEye } from "react-icons/ai";
import { BiMouseAlt } from "react-icons/bi";
import { MdOutlineEmail, MdOutlineError } from "react-icons/md";

const getEventStats = (events) => {
  return {
    delivered: events.filter((e) => e === "delivered").length,
    opened: events.filter((e) => e === "opened" || e === "unique_opened").length,
    clicked: events.filter((e) => e === "click").length,
    bounced: events.filter((e) => e === "bounce").length,
  };
};

const EmailEvents = ({ project }) => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    events: [],
    emails: [],
  });
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  async function getUsers() {
    try {
      const { ok, data } = await api.post(`/user/search`, { project_ids: [project._id], status: "active" });
      if (!ok) throw new Error(data.error);
      setUsers(data.users);
      setFilters({ ...filters, emails: data.users.map((user) => user.email) });
    } catch (e) {
      console.log(e);
    }
  }

  useEffect(() => {
    getUsers();
  }, []);

  const eventOptions = [
    { value: "request", label: "Request" },
    { value: "unique_opened", label: "Unique opened" },
    { value: "delivered", label: "Delivered" },
    { value: "opened", label: "Opened" },
  ];

  const emailOptions = users.map((user) => user.email).map((email) => ({ value: email, label: email }));

  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      search();
    }, 500);

    setSearchTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [filters]);

  useEffect(() => {}, [filters]);

  const search = async () => {
    try {
      setLoading(true);
      const { ok, data } = await api.post("/email-event/search", filters);
      if (!ok) throw new Error(data.error);
      setEvents(data);
    } catch (error) {
      toast.error("Error loading events: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedEvent(null);
  };

  return (
    <div className="">
      {/* Search Form */}
      <form className="bg-white rounded-lg shadow p-6 mb-6" onSubmit={search}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Search"
            />
          </div>
          <div className="col-span-12 md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
            <MultiSelect
              id="event-filter"
              options={eventOptions}
              values={filters.events?.map((value) => eventOptions.find((opt) => opt.value === value))}
              onSelectedChange={(selected) => {
                setFilters({
                  ...filters,
                  events: selected.map((option) => option.value),
                });
              }}
              placeholder="Filter by event type"
            />
          </div>
          <div className="col-span-12 md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Emails</label>
            <MultiSelect
              id="email-filter"
              options={emailOptions}
              values={filters.emails?.map((value) => emailOptions.find((opt) => opt.value === value))}
              onSelectedChange={(selected) => {
                setFilters({
                  ...filters,
                  emails: selected.map((option) => option.value),
                });
              }}
              placeholder="Filter by email"
            />
          </div>
          <div className="col-span-12 md:col-span-2 flex items-end">
            <button onClick={search} type="submit" disabled={loading} className="blue-btn w-full">
              {loading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>
        <div className="mt-4">
          {filters.emails?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {filters.emails.map((email) => (
                <div key={email} className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-sm">
                  <span className="text-gray-700">{email}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setFilters({
                        ...filters,
                        emails: filters.emails.filter((e) => e !== email),
                      });
                    }}
                    className="ml-2 text-gray-500 hover:text-gray-700">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </form>

      {/* Results Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Events</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {filters.emails?.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                    <div className="flex flex-col items-center justify-center space-x-2">
                      <MdOutlineEmail className="h-12 w-12 text-gray-400" />
                      <span>Please select an email address to see the events</span>
                    </div>
                  </td>
                </tr>
              ) : (
                events?.map((event, index) => {
                  const stats = getEventStats(event.events);
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{event.email}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 break-words max-w-xs">{event.subject}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(event.created_at).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center">
                            <AiOutlineEye className="text-green-600 mr-1" />
                            <span>{stats.opened}</span>
                          </div>
                          <div className="flex items-center">
                            <BiMouseAlt className="text-purple-600 mr-1" />
                            <span>{stats.clicked}</span>
                          </div>
                          <div className="flex items-center">
                            <MdOutlineError className="text-red-600 mr-1" />
                            <span>{stats.bounced}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <button
                          onClick={() => setSelectedEvent(event)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
              {events?.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                    No events found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {selectedEvent && <EmailEventDetails onClose={closeModal} event={selectedEvent} />}
    </div>
  );
};

const EmailEventDetails = ({ event, onClose }) => {
  const [content, setContent] = useState("");
  const [loadingContent, setLoadingContent] = useState(true);
  const stats = getEventStats(event.events);

  const fetchContent = async () => {
    setLoadingContent(true);
    try {
      const eventId = event.raw?.[0]?._id;
      const { ok, data } = await api.get(`/email-event/${eventId}`);
      if (!ok) throw new Error(data.error);
      setContent(data.body);
    } catch (error) {
      toast.error("Error loading content: " + error.message);
    } finally {
      setLoadingContent(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, [event]);

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case "opened":
      case "unique_opened":
        return <AiOutlineEye className="text-green-600 text-xl" />;
      case "click":
        return <BiMouseAlt className="text-purple-600 text-xl" />;
      case "bounce":
        return <MdOutlineError className="text-red-600 text-xl" />;
      case "delivered":
        return <FiMail className="text-blue-600 text-xl" />;
      case "request":
        return <FiMail className="text-gray-400 text-xl" />;
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <span className="hidden sm:inline-block sm:h-screen sm:align-middle">&#8203;</span>
        <div className="relative inline-block w-full max-w-7xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 focus:outline-none">
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Column 2: Timeline */}
            <div className="bg-gray-100 p-6 rounded-lg flex flex-col h-[600px]">
              <div className="flex flex-col mb-4">
                <div className="flex flex-1 gap-2">
                  <label className="text-sm font-medium text-gray-500">Email</label>
                  <p className="text-sm text-black">{event.email}</p>
                </div>
                <div className="flex flex-1 gap-2">
                  <label className="text-sm font-medium text-gray-500">Subject</label>
                  <p className="text-sm text-black">{event.subject}</p>
                </div>
              </div>
              <div className="flex justify-around items-start p-3 bg-white rounded-lg shadow-sm mb-4">
                <div className="flex items-center">
                  <AiOutlineEye className="text-green-600 mr-1" />
                  <span>{stats.opened}</span>
                </div>
                <div className="flex items-center">
                  <BiMouseAlt className="text-purple-600 mr-1" />
                  <span>{stats.clicked}</span>
                </div>
                <div className="flex items-center">
                  <MdOutlineError className="text-red-600 mr-1" />
                  <span>{stats.bounced}</span>
                </div>
              </div>
              <div className="space-y-3 overflow-y-auto flex-1">
                {event.raw
                  ?.sort((a, b) => new Date(b.date) - new Date(a.date))
                  ?.map((eventDetail, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-white rounded-lg shadow-sm">
                      <div className="flex-shrink-0 mt-1">{getEventIcon(eventDetail.event)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 capitalize">{eventDetail.event}</p>
                        <p className="text-sm text-gray-500">{new Date(eventDetail.date).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Column 3: Content Preview */}
            <div className="bg-gray-100 p-6 rounded-lg flex flex-col col-span-2">
              {loadingContent ? (
                <div className="animate-pulse space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                  </div>
                  <div className="space-y-2 pt-4">
                    <div className="h-3 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                  </div>
                </div>
              ) : (
                <iframe srcDoc={content} className="w-full h-full border-t" />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailEvents;
